import {
  ApplicationType,
  ApplicantRole,
  MoreApplicationsChoice,
  PartyApplicationState,
  UnionRole,
  WorksiteAddressCountry,
} from '../../../types/PartyApplication/partyApplication';
import { DID_NOT_FIND_VALUE } from 'components/formComponents/Base/Dropdown';
import { buildTerminatedEmployeesSchema } from '../steps/Form/ReportDismissedSection/utils/validation';
import { resolveRegisterDismissedValidationMessages } from '../steps/Form/ReportDismissedSection/texts';
import { z } from 'zod';
import { isValidPhoneNumberInput } from '../../../utils/validation';

export const buildFormSchema = (
  requiredConsentIds: string[] = [],
  applicantRole?: ApplicantRole,
  pageTexts?: PartyApplicationState['pageTexts']
) => {
  const applicationTypeValues = Object.values(ApplicationType);
  const applicantRoleValues = Object.values(ApplicantRole);
  const moreApplicationsValues = Object.values(MoreApplicationsChoice);
  const unionRoleValues = Object.values(UnionRole);
  const worksiteAddressCountryValues = [
    WorksiteAddressCountry.Swedish,
    WorksiteAddressCountry.Foreign,
  ];
  const companyContactRequiredApplicationTypes = [
    ApplicationType.dismissedDueToRedundancyOrContractExpiry,
    ApplicationType.dismissedDueToIllnessOrInjury,
  ];

  const optionalTrimmedString = z.string().optional();

  const partyTexts = pageTexts?.partyRegardingApplicationSection;
  const companyTexts = pageTexts?.companyDetailsSection;
  const unionTexts = pageTexts?.unionSection;
  const otherInfoTexts = pageTexts?.otherInfoSection;

  const requiredCompanyContactMessages = {
    companyContactFirstname:
      companyTexts?.companyContactFirstname?.requiredMessage ??
      'Ange kontaktpersonens förnamn',
    companyContactLastname:
      companyTexts?.companyContactLastname?.requiredMessage ??
      'Ange kontaktpersonens efternamn',
    companyContactPhone:
      companyTexts?.companyContactTelephone?.requiredMessage ??
      'Ange kontaktpersonens telefonnummer',
    companyContactEmail:
      companyTexts?.companyContactEmail?.invalidMessage ??
      'Ange en giltig e-postadress',
    companyContactConfirmEmail:
      companyTexts?.companyContactConfirmEmail?.invalidMessage ??
      'Ange en giltig e-postadress',
  } as const;

  const addRequiredStringIssue = (
    ctx: z.RefinementCtx,
    path: string,
    value: string | undefined,
    message: string
  ) => {
    if (!value || value.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: [path],
      });
    }
  };

  const isValidEmail = (value: string | undefined) => {
    if (!value || value.trim().length === 0) {
      return false;
    }
    return z.string().email().safeParse(value).success;
  };

  const normalizeEmail = (value: string | undefined): string =>
    (value ?? '').trim().toLowerCase();

  const normalizePhone = (value: string | undefined): string =>
    (value ?? '').replace(/[\s\-()]/g, '').trim();

  const terminatedEmployeesValidationMessages =
    resolveRegisterDismissedValidationMessages(
      pageTexts?.registerDismissedSection
    );

  // Base schema allows optional employment dates. Company-specific required
  // validation is added in root superRefine where applicantRole is available.
  const terminatedEmployeesSchema = buildTerminatedEmployeesSchema(
    applicantRole,
    terminatedEmployeesValidationMessages
  );

  return z
    .object({
      applicationType: z.coerce
        .number()
        .refine((value) => applicationTypeValues.includes(value), {
          message: 'Välj typ av ansökan',
        }),
      moreApplications: z.coerce
        .number()
        .refine((value) => moreApplicationsValues.includes(value), {
          message: 'Välj ett alternativ',
        }),
      partyRegardingApplication: z
        .string({
          message:
            partyTexts?.partyRegardingApplication?.requiredMessage ??
            'Välj företag som ansökan gäller',
        })
        .min(
          1,
          partyTexts?.partyRegardingApplication?.requiredMessage ??
            'Välj företag som ansökan gäller'
        ),
      worksiteRegardingApplication: z
        .string({
          message:
            partyTexts?.worksiteRegardingApplication?.requiredMessage ??
            'Välj arbetsställe som ansökan gäller',
        })
        .min(
          1,
          partyTexts?.worksiteRegardingApplication?.requiredMessage ??
            'Välj arbetsställe som ansökan gäller'
        ),
      worksitename: optionalTrimmedString,
      worksiteAddress: optionalTrimmedString,
      worksiteAddressCountry: z.coerce
        .number()
        .refine((value) => worksiteAddressCountryValues.includes(value), {
          message: 'Välj land för arbetsställets adress',
        })
        .optional(),
      worksitePostalCity: optionalTrimmedString,
      worksitePostalCode: optionalTrimmedString,
      worksiteTelephone: optionalTrimmedString,
      applicantRole: z.coerce
        .number()
        .refine((value) => applicantRoleValues.includes(value), {
          message: 'Välj din roll i ansökan',
        }),
      companyContactFirstname: optionalTrimmedString,
      companyContactLastname: optionalTrimmedString,
      companyContactEmail: optionalTrimmedString,
      companyContactConfirmEmail: optionalTrimmedString,
      companyContactPhone: optionalTrimmedString,
      companyContactAddress: optionalTrimmedString,
      companyContactCity: optionalTrimmedString,
      companyContactPostalCode: optionalTrimmedString,
      unionOrganization: z
        .string({
          message:
            unionTexts?.unionOrganization?.requiredMessage ??
            'Välj fackförbund som ansökan gäller',
        })
        .min(
          1,
          unionTexts?.unionOrganization?.requiredMessage ??
            'Välj fackförbund som ansökan gäller'
        ),
      unionRole: z.coerce
        .number()
        .refine((value) => unionRoleValues.includes(value), {
          message: 'Välj facklig roll i ansökan',
        }),
      unionContactFirstname: z.string().min(1, {
        message:
          unionTexts?.unionContactFirstname?.requiredMessage ??
          'Ange kontaktpersonens förnamn',
      }),
      unionContactLastname: z.string().min(1, {
        message:
          unionTexts?.unionContactLastname?.requiredMessage ??
          'Ange kontaktpersonens efternamn',
      }),
      unionContactEmail: z.string().email({
        message:
          unionTexts?.unionContactEmail?.invalidMessage ??
          'Ange en giltig e-postadress',
      }),
      unionContactConfirmEmail: z.string().email({
        message:
          unionTexts?.unionContactConfirmEmail?.invalidMessage ??
          'Ange en giltig e-postadress',
      }),
      unionContactPhone: z
        .string()
        .refine(
          (value) =>
            value.trim().length === 0 || isValidPhoneNumberInput(value, 7),
          {
            message:
              unionTexts?.unionContactTelephone?.requiredMessage ??
              'Ange kontaktpersonens telefonnummer',
          }
        ),
      unionContactAddress: optionalTrimmedString,
      unionContactCity: optionalTrimmedString,
      unionContactPostalCode: optionalTrimmedString,
      otherInfo: z
        .string()
        .max(
          200,
          otherInfoTexts?.otherInfoComment?.invalidMessage ??
            'Kommentaren får max innehålla 200 tecken'
        )
        .optional(),
      consent: z.record(z.string(), z.boolean()).refine(
        (value) => {
          if (requiredConsentIds.length > 0) {
            return requiredConsentIds.every((id) => value[id] === true);
          }
          return (
            Object.keys(value).length > 0 && Object.values(value).every(Boolean)
          );
        },
        {
          message:
            'Du måste godkänna samtycket för att kunna skicka in ansökan',
        }
      ),
      terminatedEmployees: terminatedEmployeesSchema,
    })
    .superRefine((data, ctx) => {
      if (data.worksiteRegardingApplication === DID_NOT_FIND_VALUE) {
        addRequiredStringIssue(
          ctx,
          'worksitename',
          data.worksitename,
          partyTexts?.worksitename?.requiredMessage ??
            'Ange arbetsställets namn'
        );
        if (data.worksiteAddressCountry === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Välj land för arbetsställets adress',
            path: ['worksiteAddressCountry'],
          });
        }
        if (data.worksiteAddressCountry === WorksiteAddressCountry.Swedish) {
          const zipCodeRegex = /^[0-9]{5}$/;
          const cleanedZipCode =
            data.worksitePostalCode?.replace(/\s/g, '') || '';
          if (!zipCodeRegex.test(cleanedZipCode)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                partyTexts?.worksitePostalCode?.invalidMessage ??
                'Ogiltigt postnummer, måste vara 5 siffror',
              path: ['worksitePostalCode'],
            });
          }
        }
        addRequiredStringIssue(
          ctx,
          'worksiteAddress',
          data.worksiteAddress,
          partyTexts?.worksiteAddress?.requiredMessage ??
            'Ange arbetsställets adress'
        );
        addRequiredStringIssue(
          ctx,
          'worksitePostalCity',
          data.worksitePostalCity,
          partyTexts?.worksitePostalCity?.requiredMessage ??
            'Ange arbetsställets ort'
        );
        addRequiredStringIssue(
          ctx,
          'worksitePostalCode',
          data.worksitePostalCode,
          partyTexts?.worksitePostalCode?.requiredMessage ??
            'Ange arbetsställets postnummer'
        );
        addRequiredStringIssue(
          ctx,
          'worksiteTelephone',
          data.worksiteTelephone,
          partyTexts?.worksiteTelephone?.requiredMessage ??
            'Ange arbetsställets telefonnummer'
        );
      }

      const shouldRequireCompanyContact =
        companyContactRequiredApplicationTypes.includes(
          data.applicationType as ApplicationType
        );

      if (shouldRequireCompanyContact) {
        addRequiredStringIssue(
          ctx,
          'companyContactFirstname',
          data.companyContactFirstname,
          requiredCompanyContactMessages.companyContactFirstname
        );
        addRequiredStringIssue(
          ctx,
          'companyContactLastname',
          data.companyContactLastname,
          requiredCompanyContactMessages.companyContactLastname
        );
        addRequiredStringIssue(
          ctx,
          'companyContactPhone',
          data.companyContactPhone,
          requiredCompanyContactMessages.companyContactPhone
        );

        if (
          data.companyContactPhone &&
          !isValidPhoneNumberInput(data.companyContactPhone, 7)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredCompanyContactMessages.companyContactPhone,
            path: ['companyContactPhone'],
          });
        }

        if (!isValidEmail(data.companyContactEmail)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredCompanyContactMessages.companyContactEmail,
            path: ['companyContactEmail'],
          });
        }

        if (!isValidEmail(data.companyContactConfirmEmail)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredCompanyContactMessages.companyContactConfirmEmail,
            path: ['companyContactConfirmEmail'],
          });
        }

        if (
          data.companyContactEmail &&
          data.companyContactConfirmEmail &&
          data.companyContactEmail !== data.companyContactConfirmEmail
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'E-postadresserna matchar inte',
            path: ['companyContactConfirmEmail'],
          });
        }
      }

      if (data.unionContactEmail !== data.unionContactConfirmEmail) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'E-postadresserna matchar inte',
          path: ['unionContactConfirmEmail'],
        });
      }

      const companyEmail = normalizeEmail(data.companyContactEmail);
      const companyPhone = normalizePhone(data.companyContactPhone);
      const unionEmail = normalizeEmail(data.unionContactEmail);
      const unionPhone = normalizePhone(data.unionContactPhone);

      if (companyEmail && unionEmail && companyEmail === unionEmail) {
        const duplicateEmailMessage =
          'E-postadressen kan inte vara samma för företagets och fackets kontaktperson';
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: duplicateEmailMessage,
          path: ['companyContactEmail'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: duplicateEmailMessage,
          path: ['unionContactEmail'],
        });
      }

      if (companyPhone && unionPhone && companyPhone === unionPhone) {
        const duplicatePhoneMessage =
          'Telefonnumret kan inte vara samma för företagets och fackets kontaktperson';
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: duplicatePhoneMessage,
          path: ['companyContactPhone'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: duplicatePhoneMessage,
          path: ['unionContactPhone'],
        });
      }

      data.terminatedEmployees.forEach((employee, index) => {
        const employeeEmail = normalizeEmail(employee.email);
        const employeePhone = normalizePhone(employee.phone);

        if (companyEmail && employeeEmail && companyEmail === employeeEmail) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Den uppsagdas e-postadress kan inte vara samma som företagets kontaktpersons e-postadress',
            path: ['terminatedEmployees', index, 'email'],
          });
        }

        if (unionEmail && employeeEmail && unionEmail === employeeEmail) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Den uppsagdas e-postadress kan inte vara samma som fackets kontaktpersons e-postadress',
            path: ['terminatedEmployees', index, 'email'],
          });
        }

        if (companyPhone && employeePhone && companyPhone === employeePhone) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Den uppsagdas telefonnummer kan inte vara samma som företagets kontaktpersons telefonnummer',
            path: ['terminatedEmployees', index, 'phone'],
          });
        }

        if (unionPhone && employeePhone && unionPhone === employeePhone) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Den uppsagdas telefonnummer kan inte vara samma som fackets kontaktpersons telefonnummer',
            path: ['terminatedEmployees', index, 'phone'],
          });
        }
      });
    });
};

export type PartApplicationFormInput = z.input<
  ReturnType<typeof buildFormSchema>
>;

export type FormData = z.output<ReturnType<typeof buildFormSchema>>;
